

import { Injectable, inject } from '@angular/core';
import { getSupabase, isSupabaseConfigured } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';

export interface AuditLog {
  id: string;
  created_at: string;
  action_type: string;
  description: string;
  metadata: any;
  user_name: string;
  user_email: string;
  user_role: string;
}

export interface AuditFilter {
  startDate?: string;
  endDate?: string;
  actionType: string;
  userId?: string;
  searchTerm?: string;
  page: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuditService {
  private supabase: SupabaseClient | null = getSupabase();

  async getAuditLogs(filter: AuditFilter): Promise<{ logs: AuditLog[], total_count: number }> {
    if (!isSupabaseConfigured() || !this.supabase) {
        // Mock Data for Dev
        return {
            logs: Array.from({ length: 5 }).map((_, i) => ({
                id: `mock-${i}`,
                created_at: new Date().toISOString(),
                action_type: i % 2 === 0 ? 'VIEW_ACCESS' : 'UPDATE',
                description: i % 2 === 0 ? 'Viewed Patient Record: John Doe' : 'Updated Stock: Tiger Eye',
                metadata: { key: 'value' },
                user_name: 'Mock User',
                user_email: 'user@test.com',
                user_role: 'admin'
            })),
            total_count: 5
        };
    }

    const { data, error } = await this.supabase.rpc('get_advanced_audit_logs', {
        p_start_date: filter.startDate,
        p_end_date: filter.endDate,
        p_action_type: filter.actionType === 'ALL' ? null : filter.actionType,
        p_user_id: filter.userId === 'all' ? null : filter.userId,
        p_search_term: filter.searchTerm || null,
        p_page_size: filter.pageSize,
        p_page_number: filter.page
    });

    if (error) throw error;
    return data as { logs: AuditLog[], total_count: number };
  }

  // Critical for PDPA: Log when sensitive data is VIEWED
  async logViewAccess(description: string, metadata: any = {}): Promise<void> {
      if (!this.supabase) return;
      // Fire and forget
      this.supabase.rpc('log_view_event', {
          p_description: description,
          p_metadata: metadata
      }).then(({ error }) => {
          if (error) console.error('Failed to audit log view:', error);
      });
  }

  async logDataExport(exportType: string): Promise<void> {
    if (!this.supabase) return;

    // This function calls the 'audit_data_export' RPC, which creates a 'DATA_EXPORT' log.
    this.supabase.rpc('audit_data_export', {
        p_export_type: exportType
    }).then(({ error }) => {
        if (error) console.error('Failed to audit log export:', error);
    });
  }
}